<template>
  <div class="container mx-auto px-4 py-4">
    <PageHeading
      class="mb-6"
      title="Categories"
      :description="`Total: ${categories.length} categories`"
      :crumbs="[{ label: 'Songs', to: '/songs' }, { label: 'Categories' }]"
    >
      <template #actions>
        <PagePrimaryButton @click="openAddModal">
          <PlusIcon class="h-4 w-4 shrink-0" />
          Add Category
        </PagePrimaryButton>
      </template>
    </PageHeading>

    <div class="bg-white dark:bg-gray-900 rounded-lg border border-gray-200 dark:border-gray-700">
      <div class="p-6">
        <!-- Error Message -->
        <div
          v-if="error"
          class="mb-4 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-md p-4"
        >
          <div class="flex">
            <div class="flex-shrink-0">
              <svg class="h-5 w-5 text-red-400" viewBox="0 0 20 20" fill="currentColor">
                <path
                  fill-rule="evenodd"
                  d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z"
                  clip-rule="evenodd"
                />
              </svg>
            </div>
            <div class="ml-3">
              <h3 class="text-sm font-medium text-red-800 dark:text-red-200">Error</h3>
              <div class="mt-2 text-sm text-red-700 dark:text-red-300">{{ error }}</div>
            </div>
          </div>
        </div>

        <div v-if="loading" class="text-center py-8">
          <div class="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600 mx-auto"></div>
        </div>

        <div v-else-if="categories.length === 0" class="text-center py-8">
          <p class="text-gray-500 dark:text-gray-400">
            No categories found. Add your first category to get started!
          </p>
        </div>

        <div v-else>
          <DataTable
            :value="categories"
            :loading="loading"
            striped-rows
            paginator
            :rows="rows"
            :rows-per-page-options="[10, 25, 50, 100]"
            class="p-datatable-sm"
            :pt="{
              root: { class: 'border border-gray-200 rounded-lg' }
            }"
          >
            <Column field="name" header="Name" sortable>
              <template #body="{ data }">
                <div class="text-sm font-medium text-gray-900 dark:text-white">{{ data.name }}</div>
              </template>
            </Column>

            <Column field="description" header="Description" sortable>
              <template #body="{ data }">
                <div class="text-sm text-gray-900 dark:text-gray-200">
                  {{ data.description || "-" }}
                </div>
              </template>
            </Column>

            <Column field="songCount" header="Song Count" sortable>
              <template #body="{ data }">
                <div class="text-sm text-gray-900 dark:text-gray-200">
                  {{ data.songCount || 0 }}
                </div>
              </template>
            </Column>

            <Column header="Actions" :exportable="false" style="width: 100px">
              <template #body="{ data }">
                <div class="flex items-center gap-2">
                  <button
                    class="text-blue-600 dark:text-blue-400 hover:text-blue-900 dark:hover:text-blue-300 p-1 rounded hover:bg-blue-50 dark:hover:bg-blue-900/20 transition-colors"
                    title="Edit category"
                    @click="openEditModal(data)"
                  >
                    <PencilIcon class="h-5 w-5" />
                  </button>
                  <button
                    :disabled="deletingCategoryId === (data.id || data.name)"
                    class="text-red-600 dark:text-red-400 hover:text-red-900 dark:hover:text-red-300 p-1 rounded hover:bg-red-50 dark:hover:bg-red-900/20 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                    :title="
                      deletingCategoryId === (data.id || data.name)
                        ? 'Deleting...'
                        : 'Delete category'
                    "
                    @click="handleDeleteCategory(data)"
                  >
                    <TrashIcon class="h-5 w-5" />
                  </button>
                </div>
              </template>
            </Column>
          </DataTable>
        </div>
      </div>
    </div>

    <!-- Add/Edit Category Modal -->
    <div
      v-if="showCategoryModal"
      class="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4"
      @click.self="cancelCategoryModal"
    >
      <div
        role="dialog"
        aria-modal="true"
        aria-labelledby="category-modal-title"
        aria-describedby="category-modal-description"
        class="relative flex max-h-[90vh] w-full max-w-md flex-col overflow-hidden rounded-lg bg-white shadow-2xl dark:bg-gray-900"
      >
        <button
          type="button"
          class="absolute right-4 top-4 z-10 rounded-sm text-gray-500 opacity-70 transition-opacity hover:opacity-100 focus:outline-none focus:ring-2 focus:ring-primary-500 dark:text-gray-400"
          aria-label="Close"
          @click="cancelCategoryModal"
        >
          <XMarkIcon class="h-4 w-4" />
        </button>

        <div
          class="shrink-0 border-b border-primary-500/10 bg-primary-500/5 p-6 dark:bg-primary-400/10"
        >
          <div class="mb-2 flex items-center gap-3 pr-8">
            <div
              class="flex h-10 w-10 items-center justify-center rounded-xl bg-primary-500/10 text-primary-600 dark:text-primary-300"
            >
              <TagIcon class="h-5 w-5" />
            </div>
            <div>
              <h2
                id="category-modal-title"
                class="text-xl font-bold tracking-tight text-gray-900 dark:text-white"
              >
                {{ editingCategory ? "Edit Category" : "Add Category" }}
              </h2>
              <p id="category-modal-description" class="text-xs text-gray-500 dark:text-gray-400">
                {{ editingCategory ? "Update category information" : "Create a new song category" }}
              </p>
            </div>
          </div>
        </div>

        <form
          class="flex min-h-0 flex-1 flex-col bg-white dark:bg-gray-900"
          @submit.prevent="handleSaveCategory"
        >
          <div class="min-h-0 flex-1 space-y-6 overflow-y-auto p-6">
            <div class="space-y-2">
              <label for="category-name" :class="categoryLabelClass">Name</label>
              <div class="relative">
                <TagIcon
                  class="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-gray-400/70"
                />
                <input
                  id="category-name"
                  v-model="categoryForm.name"
                  type="text"
                  required
                  placeholder="Enter category name"
                  :class="[categoryFieldClass, 'pl-9']"
                />
              </div>
            </div>

            <div class="space-y-2">
              <label for="category-description" :class="categoryLabelClass">Description</label>
              <textarea
                id="category-description"
                v-model="categoryForm.description"
                rows="3"
                placeholder="Enter category description (optional)"
                :class="[categoryFieldClass, 'h-auto py-2']"
              />
            </div>

            <div
              v-if="categoryError"
              class="rounded-md border border-red-200 bg-red-50 p-3 text-sm text-red-600 dark:border-red-700 dark:bg-red-900/40 dark:text-red-300"
            >
              {{ categoryError }}
            </div>
          </div>

          <div
            class="flex shrink-0 justify-end gap-3 border-t border-gray-200 px-6 py-4 dark:border-gray-700"
          >
            <button
              type="button"
              class="inline-flex h-9 items-center justify-center rounded-md px-4 text-sm font-medium text-gray-700 transition-all hover:bg-gray-100 dark:text-gray-200 dark:hover:bg-gray-800"
              @click="cancelCategoryModal"
            >
              Cancel
            </button>
            <button
              type="submit"
              :disabled="categoryLoading"
              class="inline-flex h-9 items-center justify-center gap-2 rounded-md bg-primary-600 px-8 text-sm font-medium text-white shadow-lg shadow-primary-500/20 transition-all hover:scale-[1.02] hover:bg-primary-700 disabled:pointer-events-none disabled:opacity-50 disabled:hover:scale-100"
            >
              <i class="pi pi-folder"></i>
              {{
                categoryLoading ? "Saving..." : editingCategory ? "Save Changes" : "Add Category"
              }}
            </button>
          </div>
        </form>
      </div>
    </div>

    <!-- Delete Confirmation Modal -->
    <div
      v-if="showDeleteModal"
      class="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50"
      @click.self="cancelDelete"
    >
      <div class="bg-white rounded-lg border border-gray-200 max-w-md w-full mx-4">
        <div class="p-6">
          <div class="flex items-center mb-4">
            <div
              class="flex-shrink-0 mx-auto flex items-center justify-center h-12 w-12 rounded-full bg-red-100"
            >
              <svg
                class="h-6 w-6 text-red-600"
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
              >
                <path
                  stroke-linecap="round"
                  stroke-linejoin="round"
                  stroke-width="2"
                  d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z"
                />
              </svg>
            </div>
          </div>
          <h3 class="text-lg font-medium text-gray-900 text-center mb-2">Delete Category</h3>
          <p class="text-sm text-gray-500 text-center mb-6">
            Are you sure you want to delete <strong>{{ categoryToDelete?.name }}</strong
            >? This action cannot be undone.
          </p>
          <div class="flex space-x-3">
            <Button label="Cancel" severity="secondary" class="flex-1" @click="cancelDelete" />
            <Button
              :disabled="deletingCategoryId !== null"
              :label="deletingCategoryId !== null ? 'Deleting...' : 'Delete'"
              severity="danger"
              :loading="deletingCategoryId !== null"
              class="flex-1"
              @click="confirmDelete"
            />
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { ref, onMounted } from "vue";
import { useToast } from "primevue/usetoast";
import DataTable from "primevue/datatable";
import Column from "primevue/column";
import Button from "primevue/button";
import { PencilIcon, PlusIcon, TagIcon, TrashIcon, XMarkIcon } from "@heroicons/vue/24/outline";
import api from "../../services/api";
import PageHeading from "../../components/common/PageHeading.vue";
import PagePrimaryButton from "../../components/common/PagePrimaryButton.vue";

export default {
  name: "AdminCategories",
  components: {
    DataTable,
    Column,
    Button,
    PencilIcon,
    PlusIcon,
    TagIcon,
    TrashIcon,
    XMarkIcon,
    PageHeading,
    PagePrimaryButton
  },
  setup() {
    const toast = useToast();
    const categories = ref([]);
    const loading = ref(true);
    const error = ref("");
    const showCategoryModal = ref(false);
    const editingCategory = ref(null);
    const categoryForm = ref({
      name: "",
      description: ""
    });
    const categoryError = ref("");
    const categoryLoading = ref(false);
    const showDeleteModal = ref(false);
    const categoryToDelete = ref(null);
    const deletingCategoryId = ref(null);
    const rows = ref(10);
    const categoryLabelClass =
      "text-xs font-bold uppercase tracking-wider text-gray-500 dark:text-gray-400";
    const categoryFieldClass =
      "h-11 w-full rounded-md border border-gray-300 bg-transparent px-3 text-sm text-gray-900 shadow-sm outline-none transition-all placeholder:text-gray-400 focus:border-primary-500 focus:ring-[3px] focus:ring-primary-500/20 dark:border-gray-600 dark:bg-gray-900 dark:text-white";

    const fetchCategories = async () => {
      try {
        loading.value = true;
        error.value = "";
        const response = await api.get("/api/categories");
        if (response.data.success) {
          categories.value = response.data.data.categories || [];
        } else {
          error.value = response.data.message || "Failed to fetch categories";
        }
      } catch (err) {
        console.error("Failed to fetch categories:", err);
        error.value = err.response?.data?.message || "Failed to fetch categories";
      } finally {
        loading.value = false;
      }
    };

    const openAddModal = () => {
      editingCategory.value = null;
      categoryForm.value = {
        name: "",
        description: ""
      };
      categoryError.value = "";
      showCategoryModal.value = true;
    };

    const openEditModal = (category) => {
      editingCategory.value = category;
      categoryForm.value = {
        name: category.name,
        description: category.description || ""
      };
      categoryError.value = "";
      showCategoryModal.value = true;
    };

    const cancelCategoryModal = () => {
      showCategoryModal.value = false;
      editingCategory.value = null;
      categoryForm.value = {
        name: "",
        description: ""
      };
      categoryError.value = "";
    };

    const handleSaveCategory = async () => {
      try {
        categoryLoading.value = true;
        categoryError.value = "";

        const url = editingCategory.value
          ? `/api/categories/${editingCategory.value.id || editingCategory.value.name}`
          : "/api/categories";

        const method = editingCategory.value ? "put" : "post";

        const response = await api[method](url, {
          name: categoryForm.value.name,
          description: categoryForm.value.description
        });

        if (response.data.success) {
          toast.add({
            severity: "success",
            summary: "Success",
            detail: editingCategory.value
              ? "Category updated successfully"
              : "Category created successfully",
            life: 3000
          });

          await fetchCategories();
          cancelCategoryModal();
        } else {
          categoryError.value = response.data.message || "Failed to save category";
        }
      } catch (err) {
        console.error("Failed to save category:", err);
        categoryError.value =
          err.response?.data?.message ||
          err.response?.data?.errors?.join(", ") ||
          "Failed to save category";
      } finally {
        categoryLoading.value = false;
      }
    };

    const handleDeleteCategory = (category) => {
      categoryToDelete.value = category;
      showDeleteModal.value = true;
    };

    const cancelDelete = () => {
      showDeleteModal.value = false;
      categoryToDelete.value = null;
    };

    const confirmDelete = async () => {
      if (!categoryToDelete.value) return;

      try {
        deletingCategoryId.value = categoryToDelete.value.id || categoryToDelete.value.name;
        error.value = "";

        const response = await api.delete(`/api/categories/${deletingCategoryId.value}`);

        if (response.data.success) {
          categories.value = categories.value.filter(
            (c) => (c.id || c.name) !== deletingCategoryId.value
          );
          showDeleteModal.value = false;
          categoryToDelete.value = null;
        } else {
          error.value = response.data.message || "Failed to delete category";
        }
      } catch (err) {
        console.error("Failed to delete category:", err);
        error.value = err.response?.data?.message || "Failed to delete category";
      } finally {
        deletingCategoryId.value = null;
      }
    };

    onMounted(() => {
      fetchCategories();
    });

    return {
      categories,
      loading,
      error,
      fetchCategories,
      showCategoryModal,
      editingCategory,
      categoryForm,
      categoryError,
      categoryLoading,
      openAddModal,
      openEditModal,
      cancelCategoryModal,
      handleSaveCategory,
      handleDeleteCategory,
      showDeleteModal,
      categoryToDelete,
      deletingCategoryId,
      cancelDelete,
      confirmDelete,
      rows,
      categoryLabelClass,
      categoryFieldClass
    };
  }
};
</script>
